import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  Play,
  Loader2,
  AlertTriangle,
  RotateCcw,
  VolumeX,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import "./VideoWidget.css";

/**
 * Demo video widget — landing, dashboard, debate.
 */
const VideoWidget = ({
  mode = "collapsible",
  title,
  subtitle,
  badge = "DEMO",
  cornerBadge,
  accentColor = "red",
  videoSrc,
  poster,
  mockPanel,
  initiallyOpen = false,
  className = "",
}) => {
  const { user } = useAuth();
  const isAlwaysOpen = mode === "always-open";
  const [expanded, setExpanded] = useState(isAlwaysOpen || initiallyOpen);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [mutedFallback, setMutedFallback] = useState(false);
  const videoRef = useRef(null);
  const pendingPlayRef = useRef(false);
  const userInitiatedRef = useRef(false);

  // Play the video. User gesture (play button) allows sound;
  // if the browser still blocks it, fall back to muted playback so it never stalls.
  const attemptPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    userInitiatedRef.current = true;
    setLoading(true);
    try {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          try {
            v.muted = true;
            setMutedFallback(true);
            const retry = v.play();
            if (retry && typeof retry.catch === "function") {
              retry.catch(() => { });
            }
          } catch (_) {
            // Native controls remain available as a manual fallback.
          }
        });
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const pauseVideo = useCallback(() => {
    const v = videoRef.current;
    userInitiatedRef.current = false;
    if (v) {
      try {
        v.pause();
      } catch (_) {
        // ignore
      }
    }
  }, []);

  // Start playback only after expand when the user clicked play (not chevron/row).
  useEffect(() => {
    if (!expanded || !videoSrc || !pendingPlayRef.current) return undefined;
    pendingPlayRef.current = false;
    const id = window.requestAnimationFrame(() => attemptPlay());
    return () => window.cancelAnimationFrame(id);
  }, [expanded, videoSrc, attemptPlay]);

  const handleExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  // The collapsed row is the thumbnail. Clicking anywhere on it should open
  // the player and start playback in one gesture, not open an empty stage
  // that then needs a second click on play.
  const handleExpandAndPlay = useCallback(() => {
    pendingPlayRef.current = true;
    setExpanded(true);
  }, []);

  const handleCollapse = useCallback(() => {
    pendingPlayRef.current = false;
    pauseVideo();
    setExpanded(false);
  }, [pauseVideo]);

  const handleChevronClick = (e) => {
    e.stopPropagation();
    if (expanded) handleCollapse();
    else handleExpand();
  };

  const handlePlayClick = (e) => {
    e.stopPropagation();
    if (!expanded) {
      pendingPlayRef.current = true;
      handleExpand();
    } else if (playing) {
      pauseVideo();
    } else {
      attemptPlay();
    }
  };

  const unmute = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      const v = videoRef.current;
      if (!v) return;
      v.muted = false;
      setMutedFallback(false);
      if (v.paused) attemptPlay();
    },
    [attemptPlay],
  );

  const reloadVideo = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      const v = videoRef.current;
      setErrored(false);
      setLoading(true);
      if (v) {
        try {
          v.load();
          attemptPlay();
        } catch (_) {
          // ignore
        }
      }
    },
    [attemptPlay],
  );

  // Esc collapses the widget when expanded (collapsible mode only).
  useEffect(() => {
    if (isAlwaysOpen || !expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleCollapse();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, isAlwaysOpen, handleCollapse]);

  // Shared <video> event handlers for loading / error / play state.
  const videoEventHandlers = {
    onLoadStart: () => {
      setErrored(false);

      if (userInitiatedRef.current) setLoading(true);
    },
    onWaiting: () => {
      if (userInitiatedRef.current) setLoading(true);
    },
    onCanPlay: () => setLoading(false),
    onPlaying: () => {
      setLoading(false);
      setPlaying(true);
    },
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onEnded: () => setPlaying(false),
    onError: () => {
      setLoading(false);
      setErrored(true);
    },
  };

  const renderOverlays = () => (
    <>
      {loading && !errored && (
        <div
          className="video-widget__status"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="video-widget__spinner" size={34} aria-hidden="true" />
          <span className="video-widget__status-text">Loading video…</span>
        </div>
      )}
      {errored && (
        <div className="video-widget__status video-widget__status--error" role="alert">
          <AlertTriangle size={28} aria-hidden="true" />
          <span className="video-widget__status-text">
            Couldn&rsquo;t load the video.
          </span>
          <button
            type="button"
            className="video-widget__retry"
            onClick={reloadVideo}
          >
            <RotateCcw size={15} aria-hidden="true" /> Try again
          </button>
        </div>
      )}
      {mutedFallback && !errored && playing && (
        <button
          type="button"
          className="video-widget__unmute"
          onClick={unmute}
        >
          <VolumeX size={15} aria-hidden="true" /> Tap to unmute
        </button>
      )}
    </>
  );

  // Free tier only: guests, and signed-in accounts that have never had a
  // plan. Anyone else has already seen what the product does.
  //
  // - Trial accounts in this app are not only status "trialing": the invite
  //   flows create them as "admin_invited" or "tester" with plan "free", so
  //   those statuses count as trial regardless of plan.
  // - A plan account is any account whose plan is not "free", whatever the
  //   subscription status — active, in a payment grace period, or canceled.
  //   A canceled Essentials subscriber is a former customer, not free tier.
  const subscriptionStatus = (user?.subscriptionStatus || "")
    .toString()
    .trim()
    .toLowerCase();
  const subscriptionPlan = (user?.subscriptionPlan || "")
    .toString()
    .trim()
    .toLowerCase();
  const TRIAL_STATUSES = ["trialing", "tester", "admin_invited"];
  const isTrialAccount = TRIAL_STATUSES.includes(subscriptionStatus);
  const hasPlan = !!subscriptionPlan && subscriptionPlan !== "free";

  if (user && (isTrialAccount || hasPlan)) {
    return null;
  }

  const accentClass = `video-widget--accent-${accentColor}`;

  // ─── Always-open variant (homepage) ──────────────────────────────────────
  if (isAlwaysOpen) {
    return (
      <div className={`video-widget video-widget--open ${accentClass} ${className}`}>
        {cornerBadge && (
          <span className="video-widget__corner-badge">{cornerBadge}</span>
        )}
        <div className="video-widget__open-inner">
          {videoSrc ? (
            <video
              ref={videoRef}
              className="video-widget__video"
              poster={poster}
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              controls={playing}
              {...videoEventHandlers}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          ) : poster ? (
            <img
              src={poster}
              alt={title || "Video preview"}
              className="video-widget__poster"
            />
          ) : mockPanel ? (
            <div className="video-widget__mock">{mockPanel}</div>
          ) : (
            <div className="video-widget__mock video-widget__mock--empty" />
          )}
          {videoSrc && renderOverlays()}
          {!playing && !errored && (
            <button
              type="button"
              className="video-widget__play-overlay"
              onClick={handlePlayClick}
              aria-label={`Play ${title || "video"}`}
            >
              <span className="video-widget__play-circle">
                <Play size={28} fill="currentColor" />
              </span>
              {(title || subtitle) && (
                <span className="video-widget__play-caption">
                  {title && (
                    <span className="video-widget__play-title">{title}</span>
                  )}
                  {subtitle && (
                    <span className="video-widget__play-subtitle">
                      {subtitle}
                    </span>
                  )}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Collapsible variant (research / debate pages) ───────────────────────
  return (
    <div
      className={`video-widget video-widget--collapsible ${accentClass} ${expanded ? "video-widget--is-open" : ""
        } ${className}`}
    >
      <div
        className="video-widget__row"
        onClick={expanded ? undefined : handleExpandAndPlay}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (expanded) handleCollapse();
            else handleExpandAndPlay();
          }
        }}
      >
        <span className="video-widget__thumb" onClick={handlePlayClick}>
          {badge && (
            <span className="video-widget__thumb-badge">{badge}</span>
          )}
          <span className="video-widget__thumb-play" aria-hidden="true">
            <Play size={22} fill="currentColor" />
          </span>
        </span>
        <div className="video-widget__text">
          {title && <div className="video-widget__title">{title}</div>}
          {subtitle && <div className="video-widget__subtitle">{subtitle}</div>}
        </div>
        <button
          type="button"
          className="video-widget__chevron"
          onClick={handleChevronClick}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse video" : "Expand video"}
        >
          {expanded ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
        </button>
      </div>

      {expanded && (
        <div className="video-widget__stage video-widget__stage--collapsible">
          {videoSrc ? (
            <>
              <video
                ref={videoRef}
                className="video-widget__video"
                poster={poster}
                playsInline
                webkit-playsinline="true"
                preload="metadata"
                controls
                {...videoEventHandlers}
              >
                <source src={videoSrc} type="video/mp4" />
              </video>
              {renderOverlays()}
            </>
          ) : (
            <div className="video-widget__placeholder">
              <Play size={40} />
              <span>Video coming soon</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoWidget;
